<template>
  <v-container>
    <v-card class="custom-card">
      <v-toolbar
        color="bg-primary-lighten-1"
        density="compact"
        class="px-5 py-3"
      >
        <v-icon
          icon="mdi-account-group"
          size="large"
          class="me-2"
        ></v-icon>
        <v-toolbar-title class="text-h5 font-weight-medium">
          {{ $t('users.title') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          prepend-icon="mdi-plus"
          @click="openCreateDialog"
        >
          {{ $t('users.add') }}
        </v-btn>
      </v-toolbar>

      <!-- Filters -->
      <v-card-text class="py-6">
        <v-row>
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="filters.search"
              :label="$t('users.search')"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-magnify"
              hide-details
              clearable
              @update:model-value="applyFilters"
              class="filter-field"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="4" v-if="showCommunityFilter">
            <v-select
              v-model="filters.community"
              :items="communities"
              :label="$t('users.communityFilter')"
              :loading="loadingCommunities"
              variant="outlined"
              density="comfortable"
              hide-details
              clearable
              @update:model-value="applyFilters"
              class="filter-field"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="4">
            <v-select
              v-model="filters.status"
              :items="statusFilterOptions"
              :label="$t('users.statusFilter')"
              variant="outlined"
              density="comfortable"
              hide-details
              clearable
              @update:model-value="applyFilters"
              class="filter-field"
            ></v-select>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Users table -->
      <v-data-table
        :headers="headers"
        :items="users"
        :loading="loading"
        :items-per-page="itemsPerPage"
        :items-per-page-options="[]"
        hide-default-footer
        class="custom-table"
      >
        <template v-slot:item="{ item }">
          <tr>
            <td>
              <div class="table-cell-content">
                <span>{{ item.phone_number || '—' }}</span>
                <v-btn
                  v-if="item.phone_number"
                  density="compact"
                  variant="text"
                  size="small"
                  icon="mdi-content-copy"
                  @click.stop="copyToClipboard(item.phone_number, 'phone')"
                ></v-btn>
              </div>
            </td>
            <td>{{ getFullName(item) || '—' }}</td>
            <td>
              <v-chip
                :color="getRoleColor(item.role)"
                size="small"
                class="text-uppercase"
              >
                {{ $t(`settings.roles.${item.role}`) }}
              </v-chip>
            </td>
            <td>
              <v-chip
                :color="item.is_active ? 'success' : 'error'"
                size="small"
                class="text-uppercase"
              >
                {{ $t(`users.statuses.${item.is_active ? 'active' : 'inactive'}`) }}
              </v-chip>
            </td>
            <td>
              <div class="coin-balance-wrapper">
                <span class="coin-balance">{{ item.coin_balance || 0 }}</span>
                <v-btn
                  icon="mdi-plus"
                  variant="text"
                  size="small"
                  color="success"
                  @click="openTopupDialog(item)"
                  class="topup-btn"
                ></v-btn>
              </div>
            </td>
            <td>
              <v-btn
                icon="mdi-pencil"
                variant="text"
                size="small"
                color="primary"
                @click="openEditDialog(item)"
              ></v-btn>
              <v-btn
                icon="mdi-delete"
                variant="text"
                size="small"
                color="error"
                @click="confirmDelete(item)"
              ></v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
      <base-pagination
          v-model:current-page="page"
          v-model:items-per-page="itemsPerPage"
          :total-items="totalItems"
          :items-per-page-options="[10, 25, 50, 100]"
        ></base-pagination>
    </v-card>

    <!-- Create user dialog -->
    <v-dialog v-model="createDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5 pa-4 pe-12">
          <span>{{ $t('users.createTitle') }}</span>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="createDialog = false"
            class="dialog-close-btn"
          ></v-btn>
        </v-card-title>

        <v-card-text>
          <v-form 
            ref="createForm" 
            v-model="createFormValid"
            @submit.prevent="handleCreate"
          >
            <v-text-field
              class="mb-6 phone-input-wrapper"
              persistent-placeholder
              placeholder="(xx) xxx-xxxx"
              v-model="phoneNumber"
              :label="$t('users.phone')"
              density="comfortable"
              :rules="[
                v => !!v || t('validation.required'),
                v => /^\(\d{2}\)\s\d{3}-\d{4}$/.test(v) || t('validation.phone')
              ]"
              @input="formatPhoneNumber"
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-phone" color="primary"></v-icon>
              </template>
              <template v-slot:prepend-inner>
                <div class="country-code">+84</div>
              </template>
            </v-text-field>

            <v-text-field
              class="mb-6"
              v-model="newUser.email"
              :label="$t('users.email')"
              type="email"
              variant="outlined"
              density="comfortable"
              :rules="[
                v => !v || /.+@.+\..+/.test(v) || t('validation.email')
              ]"
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-email" color="primary"></v-icon>
              </template>
            </v-text-field>

            <v-text-field
              class="mb-6"
              v-model="newUser.first_name"
              :label="$t('users.firstName')"
              variant="outlined"
              density="comfortable"
              :rules="[
                v => !v || v.length <= 50 || t('validation.maxLength', { max: 50 })
              ]"
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-account" color="primary"></v-icon>
              </template>
            </v-text-field>

            <v-text-field
              class="mb-6"
              v-model="newUser.last_name"
              :label="$t('users.lastName')"
              variant="outlined"
              density="comfortable"
              :rules="[
                v => !v || v.length <= 50 || t('validation.maxLength', { max: 50 })
              ]"
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-account" color="primary"></v-icon>
              </template>
            </v-text-field>

            <!-- Role selection field - must be placed before password field -->
            <v-select
              v-if="showRoleSelect"
              class="mb-6"
              v-model="newUser.role"
              :items="roles"
              :label="$t('users.role')"
              item-title="text"
              item-value="value"
              variant="outlined"
              required
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-shield-account" color="primary"></v-icon>
              </template>
            </v-select>

            <!-- Password field - only shown when role is manager -->
            <v-text-field
              v-if="newUser.role === 'manager'"
              class="mb-6"
              v-model="newUser.password"
              :label="$t('users.password')"
              :type="showPassword ? 'text' : 'password'"
              required
              variant="outlined"
              density="comfortable"
              :error-messages="passwordError"
              :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
              @click:append="showPassword = !showPassword"
              :rules="[
                v => !!v || t('validation.required'),
                v => v.length >= 8 || t('validation.minLength', { min: 8 })
              ]"
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-lock" color="primary"></v-icon>
              </template>
            </v-text-field>

            <v-select
              class="mb-6"
              v-model="newUser.communities"
              :loading="loadingCommunities"
              :items="communities"
              :label="$t('users.communities')"
              item-title="title"
              item-value="value"
              variant="outlined"
              multiple
              chips
              closable-chips
              required
              :rules="[
                v => v.length > 0 || t('validation.required')
              ]"
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-office-building-marker" color="primary"></v-icon>
              </template>
            </v-select>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey-darken-1"
            variant="text"
            @click="createDialog = false"
          >
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="creating"
            @click="handleCreate"
          >
            {{ $t('common.create') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit user dialog -->
    <v-dialog v-model="editDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5 pa-4 pe-12">
          <span>{{ $t('users.editTitle') }}</span>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="editDialog = false"
            class="dialog-close-btn"
          ></v-btn>
        </v-card-title>

        <v-card-text>
          <v-form ref="editForm" @submit.prevent="handleEdit">
            <v-text-field
              class="mb-6"
              v-model="editingUser.email"
              :label="$t('users.email')"
              type="email"
              required
              variant="outlined"
              :error-messages="emailError"
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-email" color="primary"></v-icon>
              </template>
            </v-text-field>

            <v-text-field
              class="mb-6"
              v-model="editingUser.first_name"
              :label="$t('users.firstName')"
              variant="outlined"
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-account" color="primary"></v-icon>
              </template>
            </v-text-field>

            <v-text-field
              class="mb-6"
              v-model="editingUser.last_name"
              :label="$t('users.lastName')"
              variant="outlined"
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-account" color="primary"></v-icon>
              </template>
            </v-text-field>

            <v-select
              v-if="showRoleSelect"
              class="mb-6"
              v-model="editingUser.role"
              :items="roles"
              :label="$t('users.role')"
              item-title="text"
              item-value="value"
              variant="outlined"
              required
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-shield-account" color="primary"></v-icon>
              </template>
            </v-select>

            <v-select
              class="mb-6"
              v-model="editingUser.communities"
              :loading="loadingCommunities"
              :items="communities"
              :label="$t('users.communities')"
              item-title="title"
              item-value="value"
              variant="outlined"
              multiple
              chips
              closable-chips
              required
              :rules="[
                v => v.length > 0 || t('validation.required')
              ]"
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-office-building-marker" color="primary"></v-icon>
              </template>
            </v-select>

            <v-switch
              v-model="editingUser.is_active"
              :label="$t('users.isActive')"
              color="success"
              class="mb-6"
              hide-details
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-account-check" color="success"></v-icon>
              </template>
            </v-switch>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey-darken-1"
            variant="text"
            @click="editDialog = false"
          >
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="editing"
            @click="handleEdit"
          >
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="localSnackbarVisible"
      :timeout="2000"
      location="top"
    >
      {{ localSnackbarText }}
    </v-snackbar>

    <!-- Delete confirmation dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h5 pa-4">
          {{ $t('users.confirmDelete') }}
        </v-card-title>
        <v-card-text>
          {{ $t('users.confirmDeleteText') }}
          <div class="mt-2 text-body-1">{{ getFullName(userToDelete) || userToDelete?.phone_number }}</div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey-darken-1"
            variant="text"
            @click="deleteDialog = false"
          >
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            color="error"
            :loading="deleting"
            @click="handleDelete"
          >
            {{ $t('common.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Topup balance dialog -->
    <v-dialog v-model="topupDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h5 pa-4 pe-12">
          <span>{{ $t('users.topupBalance') }}</span>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="topupDialog = false"
            class="dialog-close-btn"
          ></v-btn>
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            <span class="text-body-1">{{ $t('users.topupFor') }}: </span>
            <strong>{{ getFullName(userToTopup) || userToTopup?.phone_number }}</strong>
          </div>
          <v-form ref="topupForm" v-model="topupFormValid" @submit.prevent="handleTopup">
            <v-text-field
              v-model="topupAmount"
              :label="$t('users.coinsAmount')"
              type="number"
              variant="outlined"
              density="comfortable"
              :rules="[
                v => !!v || t('validation.required'),
                v => v > 0 || t('validation.minValue', { min: 1 }),
                v => v <= 2147483647 || t('validation.maxValue', { max: 2147483647 })
              ]"
              :min="1"
              :max="2147483647"
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-coin" color="primary"></v-icon>
              </template>
            </v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey-darken-1"
            variant="text"
            @click="topupDialog = false"
          >
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            color="success"
            :loading="toppingUp"
            :disabled="!topupFormValid"
            @click="handleTopup"
          >
            {{ $t('common.ok') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/user'
import api from '@/services/api'
import { useSnackbar } from '@/composables/useSnackbar'
import BasePagination from '@/components/BasePagination.vue'

const { t, locale } = useI18n()
const userStore = useUserStore()
const { showSnackbar } = useSnackbar()

const users = ref([])
const loading = ref(false)
const page = ref(1)
const itemsPerPage = ref(10)
const totalItems = ref(0)
const errorMessage = ref('')

const communities = ref([])
const loadingCommunities = ref(false)

const filters = ref({
  search: '',
  community: null,
  status: null
})

const statusFilterOptions = [
  { title: t('users.statuses.active'), value: true },
  { title: t('users.statuses.inactive'), value: false }
]

const headers = computed(() => [
  { title: t('users.phone'), key: 'phone_number' },
  { title: t('users.fullName'), key: 'name' },
  { title: t('users.role'), key: 'role' },
  { title: t('users.status'), key: 'is_active' },
  { title: t('users.coinBalance'), key: 'coin_balance' },
  { title: t('users.actions'), key: 'actions', sortable: false }
])

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return new Intl.DateTimeFormat(locale.value, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(date)
}

const fetchUsers = async () => {
  loading.value = true
  try {
    const params = {
      page: page.value,
      page_size: itemsPerPage.value,
      search: filters.value.search || undefined,
      community: filters.value.community || undefined,
      is_active: filters.value.status !== null ? filters.value.status : undefined
    }
    
    const response = await api.get('/api/v1/user/', { params })
    users.value = response.data.results
    totalItems.value = response.data.count
  } catch (error) {
    console.error('Failed to fetch users:', error)
  } finally {
    loading.value = false
  }
}

const handlePageChange = (newPage) => {
  page.value = newPage
  fetchUsers()
}

const createDialog = ref(false)
const createForm = ref(null)
const createFormValid = ref(false)
const creating = ref(false)
const showPassword = ref(false)
const emailError = ref('')
const phoneError = ref('')
const passwordError = ref('')

const newUser = ref({
  email: '',
  phone_number: '',
  first_name: '',
  last_name: '',
  password: '',
  role: 'customer',
  communities: []
})

const roles = computed(() => {
  if (userStore.profile?.role === 'administrator') {
    return [
      { text: t('settings.roles.manager'), value: 'manager' },
      { text: t('settings.roles.customer'), value: 'customer' }
    ]
  }
  return []
})

const showRoleSelect = computed(() => {
  return userStore.profile?.role === 'administrator'
})

const openCreateDialog = () => {
  createDialog.value = true
  emailError.value = ''
  phoneError.value = ''
  passwordError.value = ''
  newUser.value = {
    email: '',
    phone_number: '',
    first_name: '',
    last_name: '',
    password: '',
    role: 'customer',
    communities: []
  }
}

const phoneNumber = ref('')

const formatPhoneNumber = (e) => {
  let value = e.target.value.replace(/\D/g, '')
  const match = value.match(/^(\d{0,2})(\d{0,3})(\d{0,4})/)
  if (!match) return

  let formatted = ''
  if (match[1]) {
    formatted += `(${match[1]}`
    if (match[2]) {
      formatted += `) ${match[2]}`
      if (match[3]) {
        formatted += `-${match[3]}`
      }
    }
  }

  phoneNumber.value = formatted
}

const localSnackbarVisible = ref(false)
const localSnackbarText = ref('')

const handleCreate = async () => {
  if (!createForm.value.validate()) return
  
  try {
    loading.value = true
    const userData = {
      phone_number: `+84${phoneNumber.value.replace(/\D/g, '')}`,
      password: newUser.value.password,
      first_name: newUser.value.first_name,
      last_name: newUser.value.last_name,
      communities: newUser.value.communities,
      role: newUser.value.role,
    }
    
    if (newUser.value.email?.trim()) {
      userData.email = newUser.value.email
    }
    
    const response = await api.post('/api/v1/user/', userData)

    showSnackbar(t('users.createSuccess'))
    createDialog.value = false
    phoneNumber.value = ''
    newUser.value = {
      email: '',
      phone_number: '',
      first_name: '',
      last_name: '',
      password: '',
      role: 'customer',
      communities: []
    }
    fetchUsers()
  } catch (error) {
    console.error('Failed to create user:', error)
    if (error.response?.data?.detail) {
      showSnackbar(t('users.phoneExists'), 'error')
    } else {
      showSnackbar(t('users.createError'), 'error')
    }
  } finally {
    loading.value = false
  }
}

const editDialog = ref(false)
const editForm = ref(null)
const editing = ref(false)
const editingUser = ref({
  id: null,
  email: '',
  first_name: '',
  last_name: '',
  role: '',
  is_active: true,
  communities: []
})

const openEditDialog = (user) => {
  editDialog.value = true
  emailError.value = ''
  editingUser.value = {
    id: user.id,
    email: user.email,
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    role: user.role,
    is_active: user.is_active,
    communities: user.communities?.map(c => c.id) || []
  }
}

const handleEdit = async () => {
  emailError.value = ''
  editing.value = true

  try {
    await api.put(`/api/v1/user/${editingUser.value.id}/`, editingUser.value)
    editDialog.value = false
    await fetchUsers()
  } catch (error) {
    const errors = error.response?.data
    if (errors?.email) {
      emailError.value = Array.isArray(errors.email) ? errors.email[0] : errors.email
    }
    console.error('Failed to update user:', error)
  } finally {
    editing.value = false
  }
}

const deleteDialog = ref(false)
const deleting = ref(false)
const userToDelete = ref(null)

// Topup balance variables
const topupDialog = ref(false)
const toppingUp = ref(false)
const userToTopup = ref(null)
const topupAmount = ref('')
const topupForm = ref(null)
const topupFormValid = ref(false)

const confirmDelete = (user) => {
  userToDelete.value = user
  deleteDialog.value = true
}

const openTopupDialog = (user) => {
  userToTopup.value = user
  topupAmount.value = ''
  topupDialog.value = true
}

const handleTopup = async () => {
  if (!topupForm.value.validate()) return
  
  toppingUp.value = true
  try {
    const topupData = {
      user: userToTopup.value.id,
      coins_received: parseInt(topupAmount.value)
    }
    
    await api.post('/api/v1/payment/purchase/', topupData)
    
    showSnackbar(t('users.topupSuccess'))
    topupDialog.value = false
    topupAmount.value = ''
    await fetchUsers() // Refresh the users list to show updated balance
  } catch (error) {
    console.error('Failed to topup balance:', error)
    showSnackbar(t('users.topupError'), 'error')
  } finally {
    toppingUp.value = false
  }
}

const handleDelete = async () => {
  if (!userToDelete.value) return
  
  deleting.value = true
  try {
    await api.delete(`/api/v1/user/${userToDelete.value.id}/`)
    deleteDialog.value = false
    localSnackbarText.value = t('users.deleteSuccess')
    showSnackbar(t('users.deleteSuccess'))
    await fetchUsers()
  } catch (error) {
    console.error('Failed to delete user:', error)
    localSnackbarText.value = t('users.deleteError')
    showSnackbar(t('users.deleteError'), 'error')
  } finally {
    deleting.value = false
    userToDelete.value = null
  }
}

const getFullName = (user) => {
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`
  } else if (user.first_name) {
    return user.first_name
  } else if (user.last_name) {
    return user.last_name
  }
  return ''
}

const getRoleColor = (role) => {
  const colors = {
    administrator: 'error',
    manager: 'warning',
    user: 'success'
  }
  return colors[role] || 'grey'
}

const copyToClipboard = async (text, type) => {
  try {
    await navigator.clipboard.writeText(text)
    localSnackbarText.value = t('common.copied', { field: t(`users.${type}`) })
    showSnackbar(t('common.copied', { field: t(`users.${type}`) }))
  } catch (err) {
    console.error('Failed to copy:', err)
    localSnackbarText.value = t('common.copyError')
    showSnackbar(t('common.copyError'))
  }
}

const applyFilters = () => {
  page.value = 1
  fetchUsers()
}

const fetchCommunities = async () => {
  loadingCommunities.value = true
  try {
    const response = await api.get('/api/v1/community/')
    communities.value = response.data.results.map(community => ({
      title: community.title || community.name,
      value: community.id
    }))
    
    if (userStore.profile && userStore.profile.role === 'manager' && communities.value.length === 1) {
      filters.value.community = communities.value[0].value
    }
  } catch (error) {
    console.error('Failed to fetch communities:', error)
  } finally {
    loadingCommunities.value = false
  }
}

const showCommunityFilter = computed(() => {
  if (userStore.profile && userStore.profile.role === 'administrator') {
    return true
  }
  
  if (userStore.profile && userStore.profile.role === 'manager') {
    return communities.value.length > 1
  }
  
  return true
})

onMounted(async () => {
  loading.value = true
  try {
    await userStore.fetchProfile()
    await Promise.all([
      fetchCommunities(),
      fetchUsers()
    ])
  } catch (error) {
    console.error('Failed to initialize:', error)
  } finally {
    loading.value = false
  }
})

watch([page, itemsPerPage], () => {
  fetchUsers()
})

// Reset form when dialog closes
watch(createDialog, (val) => {
  if (!val) {
    newUser.value = {
      email: '',
      phone_number: '',
      first_name: '',
      last_name: '',
      password: '',
      role: 'customer',
      communities: []
    }
  }
})
</script>

<style scoped>
.v-container {
  max-width: 1200px !important;
  margin-left: auto;
  margin-right: auto;
}

.custom-card {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.header-section {
  background: linear-gradient(145deg, rgba(var(--v-theme-primary), 0.08), transparent 80%);
  position: relative;
  padding: 0 16px;
}

.header-border {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(
    to right,
    rgba(var(--v-theme-primary), 0.2),
    rgba(var(--v-theme-primary), 0.1),
    transparent
  );
}

.filter-field {
  margin-bottom: 16px;
}

.dialog-close-btn {
  position: absolute !important;
  right: 8px;
  top: 8px;
}

.v-card-title {
  position: relative;
}

.v-field input {
  font-family: monospace;
  letter-spacing: 0.5px;
}

.country-code {
  font-family: monospace;
  font-size: 1rem;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.9);
  padding: 0 8px;
  border-right: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

.phone-input-wrapper {
  position: relative;
}

.add-user-btn {
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)) 0%, rgb(var(--v-theme-primary)) 50%, rgba(var(--v-theme-primary), 0.9) 100%);
  border-radius: 12px;
  padding: 0 20px;
  height: 42px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: none;
  transition: all 0.3s ease;
}

.add-user-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(var(--v-theme-primary), 0.3);
}

.add-user-btn .add-icon {
  margin-right: 4px;
  font-size: 20px;
  transition: transform 0.3s ease;
}

.add-user-btn:hover .add-icon {
  transform: rotate(90deg);
}

.email-cell {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.v-btn.v-btn--density-compact {
  opacity: 0.7;
}

:deep(.v-toolbar) {
  border-bottom: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.table-search {
  width: 300px;
}

.coin-balance-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.coin-balance {
  font-weight: 500;
  color: rgba(var(--v-theme-primary), 0.8);
  font-family: monospace;
}

.topup-btn {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.topup-btn:hover {
  opacity: 1;
}
</style> 